<template>
  <base-content>
    <template v-if="loaded">
      <template slot="top">
        <hero-header :heading="`Project Simulation: ${project.name}`">
          <base-button exact icon-left="arrow-left" :to="{ name: 'projects.dashboard', params: { id: this.project._id } }">
            Project Dashboard
          </base-button>
        </hero-header>
      </template>
      <form @submit.prevent="submit">
        <base-card>
          <template v-slot:header>
            <base-card-heading>
              Simulation:
              <template v-if="changingName">
                <input
                  v-model="payload.name"
                  @keydown.prevent.enter="changingName = false"
                  autofocus
                  type="text">
              </template>
              <template v-else>
                {{ payload.name }}
                <a href="#" @click.prevent="changingName = true">Change</a>
              </template>
            </base-card-heading>
            <div>
              <span class="mr-2" v-if="!creating">
                Last saved at {{ $dates.toDateTime(simulation.updatedAt) }}
              </span>
              <span class="mr-2" :class="{ 'text-danger': creating || unsaved, 'text-success': !creating && !unsaved }">
                <template v-if="creating">
                  Unsaved Draft
                </template>
                <template v-else-if="unsaved">
                  Unsaved
                </template>
                <template v-else>
                  Saved
                </template>
              </span>
              <base-button :loading="saving" primary small submit>
                Save
              </base-button>
            </div>
          </template>
          <div class="row">
            <div class="col-sm-4">
              <base-field addon-right="months" label="Term" label-class="d-block text-center">
                <base-input v-model="payload.term" numeric placeholder="term" />
              </base-field>
              <base-range v-model="payload.term" max="60" min="6" />
            </div>
            <div class="col-sm-4">
              <base-field addon-left-icon="pound-sign" label="GDV" label-class="d-block text-center">
                <base-input v-model="payload.gdv" numeric placeholder="gdv" />
              </base-field>
              <base-range v-model="payload.gdv" max="6000000" min="1000" />
            </div>
            <div class="col-sm-4">
              <base-field addon-left-icon="pound-sign" label="Costs" label-class="d-block text-center">
                <base-input v-model="payload.costs" numeric placeholder="costs" />
              </base-field>
              <base-range v-model="payload.costs" max="2000000" min="1000" />
            </div>
          </div>
          <div class="border-top mb-3 pt-3 text-right">
            <base-button modal="#fundingModal" primary small>
              Add Funding
            </base-button>
          </div>
          <funding-table v-model="payload.funding" no-destroy @edit="edit" @removing="removeFunding" />
          <funding-modal :edit="editing" no-save @submitted="addFunding" />
        </base-card>
        <base-card>
          <template v-slot:header>
            <base-card-heading>
              Additional Security
            </base-card-heading>
            <base-button modal="#addSecurityModal" primary small>
              Add Security
            </base-button>
          </template>
          <securities-table v-model="payload.securities" no-remove no-save @removing="removeSecurity" @submitted="addSecurity" />
          <add-security-modal no-save @submitted="addSecurity" />
        </base-card>
        <project-loan v-model="payload.coverLimit" :loan="facility"/>
      </form>
    </template>
    <template v-else>
      <base-loader />
    </template>
  </base-content>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { SavesFunding } from '@/mixins'

import AddSecurityModal from '@/components/AddSecurityModal.vue'
import FundingModal from '@/components/FundingModal.vue'
import FundingTable from '@/components/FundingTable.vue'
import HeroHeader from '@/components/HeroHeader.vue'
import ProjectLoan from '@/components/ProjectLoan.vue'
import SecuritiesTable from '@/components/SecuritiesTable.vue'
import StatRow from '@/components/StatRow.vue'

export default {
  async created () {
    this.creating = this.$route.params.id === undefined

    await this.$store.dispatch('core/projects/get', this.$route.params.project)

    // If we're creating, get blank simulation schema and populate some values from the base project.
    // Otherwise retrieve the simulation.
    if (this.creating) {
      const project = this.$_.cloneDeep(this.project)

      this.payload = this.$_.clone(this.schema)
      this.payload.tag = this.$_.get(this.$route.query, 'tag')
      this.payload.funding = project.funding.filter(funding => funding.origination === this.isOrigination())
      this.payload.gdv = project.financials.gdv
      this.payload.project = project._id
      this.payload.term = project.projectedBuildPeriod
      this.payload.user = this.$auth.user._id

      // TODO All parts and drawdowns must be copied also.
      this.payload.funding.forEach(funding => this.updating.funding.push(this.$_.omit(funding, ['_id', 'createdAt', 'updatedAt'])))
    } else {
      await this.$store.dispatch('core/simulations/get', this.$route.params.id)

      this.payload = this.$_.cloneDeep(this.simulation)
    }

    this.loaded = true
  },

  data () {
    return {
      changingName: false,
      creating: false,
      deleting: {
        funding: [],
        securities: []
      },
      editing: null,
      firstWatch: false,
      loaded: false,
      facility: null,
      payload: {},
      saving: false,
      updating: {
        funding: [],
        securities: []
      },
      unsaved: false
    }
  },

  components: {
    AddSecurityModal,
    FundingModal,
    FundingTable,
    HeroHeader,
    ProjectLoan,
    SecuritiesTable,
    StatRow
  },

  mixins: [SavesFunding],

  computed: {
    ...mapGetters({
      schema: 'core/simulations/schema'
    }),

    ...mapState({
      funding: store => store.core.funding.entity,
      project: store => store.core.projects.entity,
      security: store => store.core.securities.entity,
      simulation: store => store.core.simulations.entity
    })
  },

  methods: {
    addFunding (funding) {
      this.pendChange('funding', funding)
    },

    removeFunding (funding) {
      this.deleting.funding.push(funding)
    },

    addSecurity (security) {
      this.pendChange('securities', security)
    },

    removeSecurity (security) {
      this.deleting.securities.push(security)
    },

    pendChange (attribute, record) {
      if (!this.$_.has(record, '_id')) {
        this.payload[attribute].push(record)
        this.updating[attribute].push(record)
      } else {
        const properties = ['payload', 'updating']
        let found = {
          payload: false,
          updating: false
        }

        for (let i in properties) {
          const property = properties[i]

          this[property][attribute].filter((f, k) => {
            if (this.$_.has(f, '_id') && f._id === this.$_.get(record, '_id')) {
              this[property][attribute][k] = f
              found[property] = true
            }
          })

          if (!found[property]) this[property][attribute].push(record)
        }
      }
    },

    calculate () {
      const facility = this.$_.cloneDeep(this.project.facility)

      // On the fly updates for the simulation.
      facility.gdv = this.payload.gdv

      this.facility = facility
    },

    checkChanges () {
      this.calculate()

      if (!this.firstWatch) {
        this.firstWatch = true
      } else {
        this.unsaved = true
      }
    },

    edit (funding) {
      this.editing = funding

      $('#fundingModal').modal('show')
    },

    isOrigination () {
      return String(this.tag).toLowerCase() === 'origination'
    },

    async saveFundings () {
      for (let i in this.updating.funding) {
        const payload = this.updating.funding[i]

        payload.gdv = this.payload.gdv
        payload.term = this.payload.term

        const funding = await this.saveFunding(payload)

        await this.$store.dispatch('core/simulations/funding/add', funding)
      }

      for (let i in this.deleting.funding) {
        const funding = this.deleting.funding[i]

        if (this.$_.has(funding, '_id')) {
          await Promise.all([
            this.$store.dispatch('core/project-funding/remove', funding._id),
            this.$store.dispatch('core/simulations/funding/pull', funding)
          ])
        }
      }
    },

    async saveSecurities () {
      for (let i in this.updating.securities) {
        const security = this.updating.securities[i]
        const creating = !this.$_.has(security, '_id')

        await this.$store.dispatch('core/securities/save', security)

        if (creating) {
          await this.$store.dispatch('core/simulations/securities/add', this.security)
        }
      }

      for (let i in this.deleting.securities) {
        const security = this.deleting.securities[i]

        if (this.$_.has(security, '_id')) {
          await Promise.all([
            this.$store.dispatch('core/securities/remove', security._id),
            this.$store.dispatch('core/simulations/securities/pull', security)
          ])
        }
      }
    },

    async submit () {
      this.saving = true

      await this.$store.dispatch('core/simulations/save', this.$_.cloneDeep(this.payload))

      // Add updating items.
      await Promise.all([
        this.saveFundings(),
        this.saveSecurities()
      ])

      this.saving = false
      this.unsaved = false

      this.$flash.success('Simulation saved.')

      // If we have created a new security, then go to the correct route.
      if (this.creating) {
        this.creating = false

        this.$router.push({
          name: 'projects.simulation.view',
          params: {
            project: this.project._id,
            id: this.simulation._id
          }
        })
      }
    }
  },

  watch: {
    payload: {
      deep: true,
      handler () {
        this.checkChanges()
      }
    },
    updating: {
      deep: true,
      handler () {
        this.checkChanges()
      }
    }
  }
}
</script>
